import type {
  TimetableScheduleTrack,
  // Deep import avoids @weareopenr/common's top-level barrel, which also re-exports its zod
  // codecs - those ship .d.cts files using TS 5 syntax this project's TS 4.9 can't parse.
} from '@weareopenr/common/build/src/validationHelper/timetable-schedule';
import { DayOfWeek } from '@weareopenr/types';
import {
  IMenuSchedule,
  IMenuSetSchedule,
  IPriceSchemeSchedule,
  ITimetableDetail,
  ITimetableSchedule,
} from 'src/api/getTimetable';
import type { IMenu } from 'src/types/menu.types';
import type { IMenuSet } from 'src/api/getMenuSets';
import type { ITimetableDaySchedule } from './groupSchedulesByDay';

export type { IMenuSchedule, IMenuSetSchedule, IPriceSchemeSchedule };

export const DAY_OF_WEEK_ORDER = Object.values(DayOfWeek);

export const isMenuSchedule = (schedule: ITimetableSchedule): schedule is IMenuSchedule => schedule.menu_id !== null;

export const isMenuSetSchedule = (schedule: ITimetableSchedule): schedule is IMenuSetSchedule =>
  schedule.menu_set_id !== undefined && schedule.menu_set_id !== null;

export const isPriceSchemeSchedule = (schedule: ITimetableSchedule): schedule is IPriceSchemeSchedule =>
  schedule.price_scheme_id !== null;

// A menu set is a group of menus shown as one, so it shares the menu track with menus.
export const isMenuTrackSchedule = (schedule: ITimetableSchedule): schedule is IMenuSchedule | IMenuSetSchedule =>
  isMenuSchedule(schedule) || isMenuSetSchedule(schedule);

// What a schedule points at. Menus and menu sets share the menu track; see getTrack.
export type TimetableEntityType = 'menu' | 'menuSet' | 'priceScheme';

// The lane a schedule sits on - one per day for menus (and menu sets), one for price schemes.
export type { TimetableScheduleTrack };

export interface TimetableEntityRef {
  type: TimetableEntityType;
  id: string;
}

export const getTrack = (entityType: TimetableEntityType): TimetableScheduleTrack =>
  entityType === 'priceScheme' ? 'price_scheme' : 'menu';

export const getEntityType = (schedule: ITimetableSchedule): TimetableEntityType => {
  if (isMenuSetSchedule(schedule)) return 'menuSet';
  return isMenuSchedule(schedule) ? 'menu' : 'priceScheme';
};

export const getScheduleTrack = (schedule: ITimetableSchedule): TimetableScheduleTrack =>
  getTrack(getEntityType(schedule));

export const getEntityId = (schedule: ITimetableSchedule): string => {
  if (isMenuSetSchedule(schedule)) return schedule.menu_set_id;
  return isMenuSchedule(schedule) ? schedule.menu_id : schedule.price_scheme_id;
};

export const getEntityRef = (schedule: ITimetableSchedule): TimetableEntityRef => ({
  type: getEntityType(schedule),
  id: getEntityId(schedule),
});

// The block fields the API expects for an entity - exactly one of menu_id/menu_set_id/price_scheme_id.
export const toEntityFields = ({
  type,
  id,
}: TimetableEntityRef): { menu_id: string } | { menu_set_id: string } | { price_scheme_id: string } => {
  if (type === 'menuSet') return { menu_set_id: id };
  return type === 'menu' ? { menu_id: id } : { price_scheme_id: id };
};

// The same, shaped as a schedule row - every field present, all but one null.
export const toScheduleEntityFields = ({
  type,
  id,
}: TimetableEntityRef): Pick<ITimetableSchedule, 'menu_id' | 'menu_set_id' | 'price_scheme_id'> => ({
  menu_id: type === 'menu' ? id : null,
  menu_set_id: type === 'menuSet' ? id : null,
  price_scheme_id: type === 'priceScheme' ? id : null,
});

// The menu track's default is a menu OR a menu set; the price track's is always a price scheme.
export const getDefaultEntity = (timetable: ITimetableDetail, track: TimetableScheduleTrack): TimetableEntityRef => {
  if (track === 'price_scheme') return { type: 'priceScheme', id: timetable.default_price_scheme_id };
  return timetable.default_menu_set_id
    ? { type: 'menuSet', id: timetable.default_menu_set_id }
    : { type: 'menu', id: timetable.default_menu_id ?? '' };
};

export const getDefaultEntityId = (timetable: ITimetableDetail, track: TimetableScheduleTrack): string =>
  getDefaultEntity(timetable, track).id;

export const isDefaultSchedule = (schedule: ITimetableSchedule, timetable: ITimetableDetail): boolean =>
  getEntityId(schedule) === getDefaultEntityId(timetable, getScheduleTrack(schedule));

// `fr` tracks split the space left after gaps are subtracted - unlike `%`, which ignores gaps.
export const toGridTemplateColumns = (items: ITimetableDaySchedule[]): string =>
  items.map((item) => `minmax(0, ${item.dayPercent}fr)`).join(' ');

const isInTimetableRanges = (ranges: { range_id: string }[], timetableRanges: ITimetableDetail['ranges']): boolean => {
  const timetableRangeIds = new Set(timetableRanges.map((range) => range.range_id));
  return ranges.some((range) => timetableRangeIds.has(range.range_id));
};

// A timetable with no ranges of its own applies to the whole brand, so every menu is in scope.
export const filterMenusByRanges = (menus: IMenu[], timetableRanges: ITimetableDetail['ranges']): IMenu[] =>
  timetableRanges.length === 0 ? menus : menus.filter((menu) => isInTimetableRanges(menu.ranges, timetableRanges));

export const filterMenuSetsByRanges = (
  menuSets: IMenuSet[],
  timetableRanges: ITimetableDetail['ranges'],
): IMenuSet[] =>
  timetableRanges.length === 0
    ? menuSets
    : menuSets.filter((menuSet) => isInTimetableRanges(menuSet.ranges, timetableRanges));

// A default menu picker holds either a menu's or a menu set's id - ids are unique across both.
export const toDefaultMenuFields = (
  id: string,
  menuSets: IMenuSet[],
): { default_menu_id: string } | { default_menu_set_id: string } =>
  menuSets.some((menuSet) => menuSet.menu_set_id === id) ? { default_menu_set_id: id } : { default_menu_id: id };
